import { useEffect, useMemo, useRef, useState } from 'react';
import type { FunctionComponent, MouseEvent } from 'react';
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi';
import { format } from 'date-fns';

type DatePickerProps = {
    onChange?: (value: string) => void | undefined;
    disabledDate?: (date: Date) => boolean;
    value?: string;
    locale?: 'en' | 'ro';
};

const localeWeekDays = {
    ro: ['Du', 'Lu', 'Ma', 'Mi', 'Jo', 'Vi', 'Sâ'],
    en: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
};

const localeMonthNames = {
    ro: [
        'Ianuarie',
        'Februarie',
        'Martie',
        'Aprilie',
        'Mai',
        'Iunie',
        'Iulie',
        'August',
        'Septembrie',
        'Octombrie',
        'Noiembrie',
        'Decembrie',
    ],

    en: [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
    ],
};

const calendarArray = (selectedYear: number, selectedMonth: number, startOfWeek: number) => {
    const getMonthLength = (year: number, month: number) => {
        const monthLastDate = new Date(year, month + 1, 0);
        return monthLastDate.getDate();
    };

    //get the first date object of the month
    const currentMonthFirstDate: Date = new Date(selectedYear, selectedMonth, 1);
    //get the day number of the week of the first date of the month
    const currentMonthFirstDay: number = currentMonthFirstDate.getDay();
    // get the current month length
    const currentMonthLastDate: number = getMonthLength(selectedYear, selectedMonth);
    // initialize a new array
    const calendarArray: Date[][] = [];
    //determine if 6 or 5 weeks have to be displayed for this month
    const numberOfWeeksToDisplay: number = currentMonthFirstDay + currentMonthLastDate > 35 ? 6 : 5;
    for (let week = 0; week < numberOfWeeksToDisplay; week++) {
        const weekArray: Date[] = [];
        const dayStartPosition: number = week * 7 - currentMonthFirstDay;
        for (let day = 1; day <= 7; day++) {
            const dayPosition = dayStartPosition + day + startOfWeek;
            weekArray.push(new Date(selectedYear, selectedMonth, dayPosition));
        }
        calendarArray.push(weekArray);
    }
    return calendarArray;
};

const DatePicker: FunctionComponent<DatePickerProps> = ({ onChange, disabledDate, value = '', locale }) => {
    // gets the today date time object
    const now: Date = useMemo(() => new Date(), []);

    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [selectedMonth, setSelectedMonth] = useState<number>(now.getMonth());
    const [selectedYear, setSelectedYear] = useState<number>(now.getFullYear());

    const startOfWeek = 1;

    // state needed to display the day view, an array of the month weeks containing an array of week dates
    const [displayedWeeks, setDisplayedWeeks] = useState<Date[][]>(
        calendarArray(selectedYear, selectedMonth, startOfWeek),
    );

    const daysIndex = Array.from(Array(7).keys()).map((i) => (i + startOfWeek) % 7);
    const weekDays = localeWeekDays[locale || 'ro'];
    const monthNames = localeMonthNames[locale || 'ro'];

    const datepickerRef = useRef<HTMLDivElement>(null);

    //if a value prop is passed to the component, update the selectedDate state
    useEffect(() => {
        if (!value) {
            setSelectedDate(null);
        } else {
            const d = new Date(value);
            setSelectedDate(d);
            setSelectedMonth(d.getMonth());
            setSelectedYear(d.getFullYear());
        }
    }, [value]);

    //get the array of weeks and dates needed for the day tab view, and update the displayedWeeks state
    useEffect(() => {
        setDisplayedWeeks(calendarArray(selectedYear, selectedMonth, startOfWeek));
    }, [selectedYear, selectedMonth]);

    const nop = () => {};

    const onDayClick = (e: MouseEvent<HTMLDivElement>) => {
        const target = e.target as HTMLDivElement;
        const dateElement = target.closest('.cursor-pointer');

        if (dateElement && dateElement instanceof HTMLElement) {
            setSelectedDate(new Date(target.id));
            if (onChange) {
                onChange(target.id);
            }
        }
    };

    const onNavClick = (e: MouseEvent<SVGElement>, direction: 'next' | 'prev') => {
        e.preventDefault();

        if (direction === 'prev') {
            if (selectedMonth === 0) {
                setSelectedMonth(11);
                setSelectedYear(selectedYear - 1);
            } else {
                setSelectedMonth(selectedMonth - 1);
            }
        }
        if (direction === 'next') {
            if (selectedMonth === 11) {
                setSelectedMonth(0);
                setSelectedYear(selectedYear + 1);
            } else {
                setSelectedMonth(selectedMonth + 1);
            }
        }
    };

    const alignClass = 'flex items-center justify-center';
    const cellClass = `w-9 h-9 ${alignClass}`;

    return (
        <div
            className="border-input-border-color flex select-none flex-col gap-2 border bg-white p-4"
            ref={datepickerRef}>
            <div className="flex items-center justify-between">
                <HiChevronLeft
                    role="button"
                    className="cursor-pointer text-xl"
                    onClick={(e) => {
                        onNavClick(e, 'prev');
                    }}
                />
                <div className={alignClass}>
                    {monthNames[selectedMonth]} {selectedYear}
                </div>
                <HiChevronRight
                    role="button"
                    className="cursor-pointer text-xl"
                    onClick={(e) => {
                        onNavClick(e, 'next');
                    }}
                />
            </div>

            <div className="border-lightest-grey flex justify-between border-b">
                {daysIndex.map((day, index) => (
                    <div key={`${day}-${index}`} className={`${cellClass} font-bold`}>
                        {weekDays[day]}
                    </div>
                ))}
            </div>

            {displayedWeeks.map((week, index) => {
                return (
                    <div className="text-primary flex justify-between" key={week.toString() + index}>
                        {week.map((date, index) => {
                            const disabled = typeof disabledDate === 'function' ? disabledDate(date) : false;
                            let className = `${!disabled ? 'cursor-pointer' : ''} ${cellClass}`.trim();

                            if (date.getMonth() !== selectedMonth) {
                                return (
                                    <div className={cellClass} key={date.toString() + index}>
                                        &nbsp;
                                    </div>
                                );
                            }

                            if (disabled) {
                                className = `${className} opacity-50`;
                            }
                            if (selectedDate && date.toDateString() === selectedDate.toDateString()) {
                                className = `${className} rounded-full bg-secondary font-bold`;
                            }
                            if (date.toDateString() === now.toDateString()) {
                                className = `${className} font-bold`;
                            }
                            return (
                                <div
                                    key={date.toString() + index}
                                    className={className}
                                    id={format(date, 'yyyy-MM-dd')}
                                    onClick={disabled ? nop : onDayClick}>
                                    {date.getDate()}
                                </div>
                            );
                        })}
                    </div>
                );
            })}
        </div>
    );
};

DatePicker.displayName = 'DatePicker';

export default DatePicker;
